.shell {
  min-height: 100dvh;
  padding: max(1rem, env(safe-area-inset-top)) max(1rem, env(safe-area-inset-right)) max(1.25rem, env(safe-area-inset-bottom)) max(1rem, env(safe-area-inset-left));
  background:
    radial-gradient(circle at 12% 8%, rgb(55 111 148 / 38%), transparent 30%),
    radial-gradient(circle at 88% 12%, rgb(191 173 129 / 9%), transparent 24%),
    linear-gradient(145deg, #102534 0%, #17384b 48%, #081522 100%);
}

.header,
.hero,
.metrics,
.tabs,
.workspace,
.tableCard,
.loading,
.error {
  width: min(1480px, 100%);
  margin-inline: auto;
}

.header {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 1rem;
}

.brand {
  justify-self: start;
  border: 0;
  padding: 0.7rem 0;
  background: transparent;
  color: var(--color-text-primary);
  font-weight: 760;
  letter-spacing: 0.18em;
  cursor: pointer;
}

.brand span,
.eyebrow { color: var(--color-gold); }

.sectionName {
  color: var(--color-text-secondary);
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.signOut {
  justify-self: end;
  min-height: 2.7rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 0.85rem;
  padding-inline: 1rem;
  background: rgb(255 255 255 / 5%);
  color: var(--color-text-primary);
  cursor: pointer;
}

.hero {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 2rem;
  padding-block: clamp(3rem, 7vh, 5rem) 2rem;
}

.hero h1 {
  margin: 0;
  font-size: clamp(2.7rem, 6vw, 5rem);
  line-height: 0.96;
  letter-spacing: -0.05em;
}

.hero p:not(.eyebrow) {
  max-width: 760px;
  color: var(--color-text-secondary);
  line-height: 1.65;
}

.eyebrow {
  margin: 0 0 0.55rem;
  font-size: 0.7rem;
  font-weight: 760;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.serverBadge {
  border: 1px solid color-mix(in srgb, var(--color-gold) 50%, transparent);
  border-radius: 999px;
  padding: 0.55rem 0.8rem;
  color: var(--color-gold);
  background: rgb(7 21 34 / 54%);
  font-size: 0.75rem;
}

.loading,
.error,
.metrics article,
.tabs,
.inviteForm,
.tokenCard,
.tableCard {
  border: 1px solid var(--color-border-soft);
  background:
    linear-gradient(155deg, rgb(255 255 255 / 8%), rgb(255 255 255 / 3%)),
    rgb(7 21 34 / 50%);
  backdrop-filter: blur(24px) saturate(118%);
}

.loading,
.error {
  margin-bottom: 1rem;
  border-radius: 1rem;
  padding: 1rem;
  color: var(--color-text-secondary);
}

.metrics {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.7rem;
  margin-bottom: 1rem;
}

.metrics article {
  display: grid;
  gap: 0.25rem;
  border-radius: 1rem;
  padding: 0.9rem 1rem;
}

.metrics span {
  color: var(--color-text-tertiary);
  font-size: 0.72rem;
}

.metrics strong {
  color: var(--color-gold);
  font-size: 1.35rem;
}

.tabs {
  display: flex;
  gap: 0.35rem;
  margin-bottom: 1rem;
  border-radius: 999px;
  padding: 0.35rem;
}

.tabs button {
  flex: 1;
  min-height: 2.6rem;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
}

.tabs button[aria-pressed='true'] {
  border: 1px solid color-mix(in srgb, var(--color-gold) 48%, transparent);
  background: color-mix(in srgb, var(--color-gold) 13%, transparent);
  color: var(--color-text-primary);
}

.workspace {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1rem;
}

.inviteForm,
.tokenCard,
.tableCard {
  border-radius: 1.2rem;
  padding: 1rem;
}

.inviteForm {
  display: grid;
  grid-template-columns: 2fr 1fr 1.4fr 1fr auto;
  align-items: end;
  gap: 0.75rem;
}

.inviteForm .sectionHeading {
  grid-column: 1 / -1;
}

.inviteForm label {
  display: grid;
  gap: 0.35rem;
  color: var(--color-text-tertiary);
  font-size: 0.72rem;
}

.inviteForm input,
.inviteForm select {
  min-height: 2.75rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 0.8rem;
  padding-inline: 0.8rem;
  outline: none;
  background: rgb(255 255 255 / 5%);
  color: var(--color-text-primary);
}

.inviteForm button,
.tableCard button {
  min-height: 2.7rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 52%, transparent);
  border-radius: 0.8rem;
  padding-inline: 0.8rem;
  background: color-mix(in srgb, var(--color-gold) 14%, transparent);
  color: var(--color-text-primary);
  cursor: pointer;
}

.inviteForm button:disabled,
.tableCard button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.tokenCard {
  display: grid;
  align-content: start;
  gap: 0.7rem;
  grid-column: 2;
  grid-row: 1;
}

.tokenCard code {
  overflow-wrap: anywhere;
  border: 1px solid var(--color-border-soft);
  border-radius: 0.8rem;
  padding: 0.8rem;
  color: var(--color-gold);
  background: rgb(4 13 24 / 45%);
}

.tokenCard small {
  color: var(--color-text-tertiary);
  line-height: 1.5;
}

.tableCard {
  grid-column: 1 / -1;
}

.sectionHeading {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.8rem;
}

.sectionHeading h2 { margin: 0; }

.sectionHeading > span {
  color: var(--color-text-tertiary);
  font-size: 0.75rem;
}

.tableScroll {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 0.75rem 0.65rem;
  border-bottom: 1px solid var(--color-border-soft);
  text-align: left;
  vertical-align: middle;
}

th {
  color: var(--color-text-tertiary);
  font-size: 0.7rem;
  font-weight: 650;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

td {
  color: var(--color-text-secondary);
  font-size: 0.82rem;
}

td strong,
td small {
  display: block;
}

td small {
  margin-top: 0.2rem;
  color: var(--color-text-tertiary);
}

.status {
  display: inline-flex;
  border: 1px solid var(--color-border-soft);
  border-radius: 999px;
  padding: 0.22rem 0.45rem;
  color: var(--color-gold);
  font-size: 0.66rem;
  text-transform: capitalize;
}

.auditList {
  display: grid;
  gap: 0.6rem;
}

.auditList article {
  display: grid;
  grid-template-columns: 1.2fr 0.7fr 1fr;
  gap: 1rem;
  border-bottom: 1px solid var(--color-border-soft);
  padding-block: 0.8rem;
}

.auditList article > div {
  display: grid;
  gap: 0.2rem;
}

.auditList span,
.auditList time,
.auditList code {
  color: var(--color-text-tertiary);
  font-size: 0.72rem;
}

.auditList code {
  overflow-wrap: anywhere;
  white-space: normal;
}

button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--color-gold) 70%, white);
  outline-offset: 2px;
}

@media (max-width: 1120px) {
  .metrics {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .workspace {
    grid-template-columns: 1fr;
  }

  .tokenCard {
    grid-column: 1;
    grid-row: auto;
  }

  .inviteForm {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 760px) {
  .header {
    grid-template-columns: 1fr auto;
  }

  .sectionName {
    display: none;
  }

  .hero {
    align-items: flex-start;
    flex-direction: column;
  }

  .metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .tabs {
    border-radius: 1rem;
  }

  .tabs button {
    min-height: 3rem;
    padding-inline: 0.5rem;
  }

  .auditList article {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 520px) {
  .inviteForm {
    grid-template-columns: 1fr;
  }

  .metrics {
    grid-template-columns: 1fr;
  }

  .tabs {
    display: grid;
  }

  .inviteForm input,
  .inviteForm select {
    font-size: 16px;
  }
}


.planGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.7rem;
  margin-bottom: 1rem;
}

.planGrid article {
  display: grid;
  gap: 0.35rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 1rem;
  padding: 0.9rem;
  background: rgb(255 255 255 / 3%);
}

.planGrid span,
.planGrid em {
  color: var(--color-gold);
  font-size: 0.72rem;
  font-style: normal;
}

.planGrid strong {
  font-size: 1.05rem;
}

.planGrid small {
  color: var(--color-text-tertiary);
  line-height: 1.45;
}

.entitlementForm {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(160px, 0.55fr) minmax(140px, 0.45fr) auto;
  align-items: end;
  gap: 0.7rem;
  margin-bottom: 1rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 1rem;
  padding: 0.9rem;
  background: rgb(7 21 34 / 36%);
}

.entitlementForm label {
  display: grid;
  gap: 0.35rem;
  color: var(--color-text-tertiary);
  font-size: 0.72rem;
}

.entitlementForm select {
  min-height: 2.75rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 0.8rem;
  padding-inline: 0.8rem;
  background: rgb(255 255 255 / 5%);
  color: var(--color-text-primary);
}

.entitlementForm button {
  min-height: 2.75rem;
  border: 1px solid color-mix(in srgb, var(--color-gold) 52%, transparent);
  border-radius: 0.8rem;
  padding-inline: 1rem;
  background: color-mix(in srgb, var(--color-gold) 14%, transparent);
  color: var(--color-text-primary);
  cursor: pointer;
}

.entitlementForm button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

@media (max-width: 820px) {
  .planGrid {
    grid-template-columns: 1fr;
  }

  .entitlementForm {
    grid-template-columns: 1fr;
  }
}


.dependentStrip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.55rem;
  margin: 0 0 1rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 1rem;
  padding: 0.8rem 0.9rem;
  background: rgb(7 21 34 / 34%);
}

.dependentStrip strong {
  margin-right: 0.25rem;
  color: var(--color-text-secondary);
  font-size: 0.78rem;
}

.dependentStrip span {
  color: var(--color-text-tertiary);
  font-size: 0.76rem;
}

.dependentStrip button {
  min-height: 2.25rem;
  border: 1px solid var(--color-border-soft);
  border-radius: 999px;
  padding-inline: 0.75rem;
  background: rgb(255 255 255 / 4%);
  color: var(--color-text-secondary);
  cursor: pointer;
}
